import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Tippy from '@tippy.js/react'
import 'react-tippy/dist/tippy.css'
import Swal from 'sweetalert2'
import { BiTrash, BiEdit } from 'react-icons/bi'
import {
	DefaultButton,
	DefaultPageTitle,
	DefaultTable,
	Loading,
	ErrorUI,
	CreateButtonDefaultContainer,
} from 'components'
import { getAllCoupons, deleteCoupon } from 'services/coupons'
import Coupon from 'models/coupon'
import { DefaultCreateButtonAndSearchFormContainer, SearchContainer, CouponsContainer } from './style'
import DefaultInput from 'components/DefaultInput'
import { CREATE_COUPONS, EDIT_COUPON_BY_ID } from 'components/Routes/Constants'

const useCOUPONS = () => {
	const [coupons, setCoupons] = useState<Coupon[]>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(false)

	const getCoupons = useCallback(async () => {
		try {
			setLoading(true)
			const couponsReponse = await getAllCoupons()
			if (couponsReponse && couponsReponse.length) {
				setCoupons(couponsReponse)
			}
			setError(false)
		} catch (err) {
			setError(true)
		}
		setLoading(false)
		
	}, [setCoupons])

	const handleDeleteCoupon = useCallback(
		async ({ coupon_id }: Coupon) => {
			setLoading(true)
			const responseFire = await Swal.fire({
				title: '<strong>Confirmação</strong>',
				html: `Tem certeza que deseja remover este cupom?`,
				showCancelButton: true,
				cancelButtonText: 'Cancelar',
				focusConfirm: false,
			})
			if (responseFire.isConfirmed) {
				await deleteCoupon(coupon_id)

				Swal.fire({
					icon: 'success',
					title: 'Sucesso!',
					text: `O cupom foi excluido com sucesso!`,
				})

				await getCoupons()
			}
			setLoading(false)
		},
		[getCoupons]
	)

	useEffect(() => {
		getCoupons()
	}, [getCoupons])

	return { coupons, loading, error, handleDeleteCoupon }
}

const CouponsUI: React.FC = () => {
	const history = useHistory()
	const { coupons, loading, error, handleDeleteCoupon } = useCOUPONS()
	const [filter, setFilter] = useState('')

	const couponFilter = useMemo(() => {
		const lowerCase = filter.toLowerCase()
		return coupons.filter(
			(coupon) =>
				coupon.name.toLowerCase().includes(lowerCase) || coupon.coupon_code.toLowerCase().includes(lowerCase)
		)
	}, [filter, coupons])

	const createCoupon = async () => {
		history.push(`${CREATE_COUPONS}`)
	}

	const editCoupon = useCallback(
		(couponId: string) => {
			const editCouponRoute = EDIT_COUPON_BY_ID(couponId)
			history.push(editCouponRoute)
		},
		[history]
	)

	const CuponsToBeShown = useMemo(
		() =>
			(couponFilter || [])
				.sort((u1, u2) => u1.name.localeCompare(u2.name))
				.map((coupon) => ({
					id: coupon.coupon_id,
					name: coupon.name,
					speciality: coupon.speciality,
					coupon_code: coupon.coupon_code,
					actions: (
						<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
							<Tippy content="Editar cupom">
								<DefaultButton
									className="small info"
									onClick={() => coupon.coupon_id && editCoupon(coupon.coupon_id.toString())}
								>
									<BiEdit />
								</DefaultButton>
							</Tippy>
							<Tippy content="Excluir cupom">
								<DefaultButton
									style={{ marginLeft: '10px' }}
									className="small danger"
									onClick={() => handleDeleteCoupon(coupon)}
								>
									<BiTrash />
								</DefaultButton>
							</Tippy>
						</div>
					),
				})),
		[couponFilter, deleteCoupon]
	)

	if (loading) {
		return <Loading />
	}

	if (error) {
		return <ErrorUI type="loadingError" />
	}

	return (
		<CouponsContainer className="coupons-list">
			<DefaultPageTitle>Lista de Cupons</DefaultPageTitle>
			<CreateButtonDefaultContainer>
				<DefaultButton onClick={createCoupon}>Criar Cupom</DefaultButton>
			</CreateButtonDefaultContainer>
			<SearchContainer>
				<DefaultCreateButtonAndSearchFormContainer style={{ justifyContent: 'flex-end' }}>
					<DefaultInput
						type="search"
						placeholder="Digite sua pesquisa aqui..."
						value={filter}
						onChange={(e) => setFilter(e.target.value)}
						style={{ margin: 0 }}
					/>
				</DefaultCreateButtonAndSearchFormContainer>
			</SearchContainer>
			<DefaultTable
				headersConfig={[
					{
						headerLabel: <span>Nome</span>,
						propName: 'name',
					},
					{
						headerLabel: <span>Especialidade</span>,
						propName: 'speciality',
					},
					{
						headerLabel: <span>Cupom</span>,
						propName: 'coupon_code',
					},
					{
						headerLabel: <span>Ações</span>,
						propName: 'actions',
					},
				]}
				items={CuponsToBeShown}
				emptyListMessage={'Não foram encontrados usuários cadastrados!'}
			/>
		</CouponsContainer>
	)
}

export default CouponsUI
